import {
  getBlockNameByScope,
  isLauncherJourney as isLauncherJourneyV3
} from '@epilot/journey-logic-commons'
import {
  isLauncherJourney,
  isPostQualificationJourney,
  isRegularJourney
} from '@epilot/journey-logic-commons/src/next/journey'
import { selectSortableMapSorted } from '@epilot/journey-logic-commons/src/next/store'
import type {
  BlockId,
  Journey,
  JourneyId,
  LANGUAGE,
  LaunchableJourney,
  LauncherJourney,
  Step,
  StepId
} from '@epilot/journey-logic-commons/src/types/next'
import { memoLastCall, objectify } from 'radashi'

import type { PerJourneyAppStateSelector, WritableAppState } from '../types'
import { APP_STATUS } from '../types/enums'

import { selectBlocksMapCache, selectCurrentStep } from './runtime'

export const selectAppStatus = (state: WritableAppState) => state.appStatus

export const selectIsAppReady = (state: WritableAppState) => {
  return selectAppStatus(state) === APP_STATUS.READY
}

export const selectPerJourneyById = (
  state: WritableAppState,
  id: JourneyId | undefined | null
) => {
  if (!id) return undefined

  return state.journeys[id]
}

/**
 * Returns the root Journey-specific state.
 * This can be a launcher-type Journey, but not necessarily.
 */
export const selectRoot = (state: WritableAppState) => {
  return selectPerJourneyById(state, state.rootJourneyId)
}

/**
 * Returns the selected Journey-specific state.
 * This can't ever be a launcher-type Journey since those can't be selected.
 */
export const selectSelected = (state: WritableAppState) => {
  return selectPerJourneyById(state, state.selectedJourneyId)
}

export const selectSelectedJourneyId = (state: WritableAppState) =>
  state.selectedJourneyId

/**
 * Similar to {@link selectSelectedJourneyId} but only returns the Journey Id
 * from the hydrated object.
 * This is useful when you need to make sure the selected Journey config is already available.
 */
export const selectHydratedJourneyId = (state: WritableAppState) => {
  return selectJourney(state)?.id
}

/**
 * Returns the original Journey (aka version 3).
 * @deprecated slowly getting replaced, prefer {@link selectJourney} whenever possible
 */
export const selectOriginalJourney = (
  state: WritableAppState,
  /**
   * Which lookup selector to use.
   * @default selectSelectedJourney
   */
  fn: PerJourneyAppStateSelector = selectSelected
) => {
  return fn(state)?.originalJourney
}

/**
 * Returns the selected original Journey, or the root original Journey.
 * This is useful for cases where a fallback to the Root journey is needed.
 */
export const selectOriginalJourneyOrRoot = (state: WritableAppState) => {
  return (
    selectOriginalJourney(state) ?? selectOriginalJourney(state, selectRoot)
  )
}

/**
 * Returns the selected Journey, or the root Journey.
 * Always in the latest schema format.
 */
export const selectJourneyOrRoot = (state: WritableAppState) => {
  return selectJourney(state) ?? selectJourney(state, selectRoot)
}

/**
 * Selects the original Journey by its ID.
 * @deprecated
 */
export const selectOriginalJourneyPerId = (
  state: WritableAppState,
  id: JourneyId | undefined
) => {
  return selectPerJourneyById(state, id)?.originalJourney
}

/**
 * Returns the transformed Journey schema (aka version 4).
 * This is the future, prefer this over {@link selectOriginalJourney} whenever possible.
 *
 * Currently, this gets created off of the original Journey config.
 */
export const selectJourney = (
  state: WritableAppState,
  /**
   * Which lookup selector to use.
   * @default selectSelectedJourney
   */
  fn: PerJourneyAppStateSelector = selectSelected
) => {
  return fn(state)?.journey
}

export const selectBlocks = (
  state: WritableAppState,
  /**
   * Which lookup selector to use.
   * @default selectSelectedJourney
   */
  fn: PerJourneyAppStateSelector = selectSelected
) => {
  return fn(state)?.journey?.blocks
}

export const selectLauncherJourney = (
  state: WritableAppState
): LauncherJourney | undefined => {
  const journey = selectRoot(state)?.journey

  if (isLauncherJourney(journey)) return journey

  return undefined
}

/**
 * Determines if the launcher journey is empty.
 * Should only be used in the context of Launcher-type journeys, otherwise throws an error.
 */
export const selectIsLauncherJourneyEmpty = (
  state: WritableAppState
): boolean => {
  const journey = selectRoot(state)?.journey

  if (isLauncherJourney(journey)) {
    return Object.keys(journey.journeys).length === 0
  }

  throw new Error('Expected a launcher Journey')
}

/**
 * Determines if a given Journey is a template through a selector function.
 *
 * This is useful for checking whether a selected or root Journey is a template, just by
 * switching the sub-selector used.
 */
export const selectIsTemplate = (
  state: WritableAppState,
  fn: (state: WritableAppState) => Readonly<Journey> | undefined
) => {
  return Boolean(fn(state)?.isTemplate)
}

/**
 * Convenience method to forcefully select a launcher-type journey.
 * Throws an error otherwise.
 *
 * WARNING: Should only be used when you're absolutely certain you're dealing with a launcher-type Journey,
 * or you've implemented the proper error handling to prevent crashes.
 *
 * This is specific to the root journey since others can't be of launcher-type.
 */
export const selectLauncherJourneyOrThrow = (
  state: WritableAppState
): LauncherJourney => {
  const journey = selectLauncherJourney(state)

  if (!journey) throw new Error('Expected a Launcher-type Journey')

  return journey
}

/**
 * Convenience selector to determine whether a Journey is of type "launcher".
 *
 * Since we haven't yet fully migrated to the new schema, we give priority to the new config and
 * use the cache and the original config as a fallback.
 */
export const selectIsRootLauncherJourney = (
  state: WritableAppState
): boolean => {
  const root = selectRoot(state)

  if (root?.journey) return isLauncherJourney(root?.journey)

  return (
    root?.isLauncherType ?? isLauncherJourneyV3(root?.originalJourney?.steps)
  )
}

export const selectLaunchableJourneysSorted = (state: WritableAppState) => {
  const launcher = selectLauncherJourney(state)

  if (!launcher) return []

  return selectSortableMapSorted<JourneyId, LaunchableJourney>(
    launcher.journeys
  )
}

export const selectJourneysByIds = (
  state: WritableAppState,
  ids: JourneyId[],
  selectorFn: typeof selectJourney | typeof selectOriginalJourney
): Exclude<ReturnType<typeof selectorFn>, undefined>[] => {
  return ids.reduce<Exclude<ReturnType<typeof selectorFn>, undefined>[]>(
    (acc, id) => {
      const journey = selectorFn(state, (s) => selectPerJourneyById(s, id))

      if (journey) acc.push(journey)

      return acc
    },
    []
  )
}

export const selectLaunchableJourneyIdsSorted = (
  state: WritableAppState
): JourneyId[] => {
  return selectLaunchableJourneysSorted(state).map(
    (journey) => journey.journeyId
  )
}

/**
 * Returns the language of the selected Journey.
 * If the selected Journey is not available or language is missing, attempts the root Journey.
 */
export const selectJourneyLanguage = (state: WritableAppState) => {
  const lang =
    selectJourney(state)?.settings?.language ??
    selectOriginalJourney(state)?.settings?.embedOptions?.lang ??
    selectJourney(state, selectRoot)?.settings?.language ??
    selectOriginalJourney(state, selectRoot)?.settings?.embedOptions?.lang

  if (lang) return lang as LANGUAGE

  return undefined
}

/**
 * Returns the Journey public token according to its type, if available.
 * Resorts to the original Journey if newest schema is not found.
 */
export const selectJourneyPublicToken = (state: WritableAppState) => {
  const journey = selectJourney(state)

  if (journey) {
    if (isRegularJourney(journey) || isPostQualificationJourney(journey)) {
      return journey.settings.publicToken
    }

    return undefined
  }

  return selectOriginalJourney(state)?.settings?.publicToken
}

export const selectJourneyOrgId = (state: WritableAppState) => {
  return (
    selectJourney(state, selectRoot)?.organizationId ??
    selectOriginalJourney(state, selectRoot)?.organizationId
  )
}

export const selectFeatureFlags = (state: WritableAppState) =>
  state.featureFlags

export const selectFeatureFlag = <T = boolean>(
  state: WritableAppState,
  name: string,
  defaultValue?: T,
  castBoolean: boolean = true
): T => {
  const flags = selectFeatureFlags(state)
  const value = castBoolean ? Boolean(flags[name]) : flags[name]

  return (value ?? defaultValue) as T
}

export const selectFeatureFlagFactory =
  <T = boolean>(name: string, defaultValue?: T, castBoolean: boolean = true) =>
  (state: WritableAppState) => {
    return selectFeatureFlag<T>(state, name, defaultValue, castBoolean)
  }

export const selectLogics = (state: WritableAppState) => {
  const journey = selectJourney(state)

  if (journey && typeof journey.logics !== 'undefined') {
    return Object.values(journey.logics)
  }

  return []
}

export const selectStepsKeyedByOrderIndex = (
  state: WritableAppState
): { [key: Step['orderIndex']]: Step } => {
  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) return {}

  return computeStepsByOrderIndexWithMemo(journey?.steps)
}

const computeStepsByOrderIndexWithMemo = memoLastCall(
  (steps: Journey['steps']) => {
    return objectify(Object.values(steps ?? {}), (item) => item.orderIndex)
  }
)

/**
 * Returns the list of Steps sorted (ASC) by order index.
 *
 * Theoretically speaking, a given Step's orderIndex should always match their index within the Steps array.
 * Still, we sort before ordering them to ensure consistency.
 */
export const selectStepsSorted = (state: WritableAppState) => {
  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) {
    throw new Error(
      'Implementation error. Launcher-type journeys have no steps.'
    )
  }

  return computeStepsSortedWithMemo(journey?.steps)
}

const computeStepsSortedWithMemo = memoLastCall((steps: Journey['steps']) => {
  return selectSortableMapSorted<StepId, Step>(steps ?? {})
})

export const selectNumberOfSteps = (state: WritableAppState) => {
  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) {
    throw new Error(
      'Implementation error. Launcher-type journeys have no steps.'
    )
  }

  return Object.keys(journey?.steps ?? {}).length
}

export const selectBlockById = (
  state: WritableAppState,
  id: BlockId | undefined
) => {
  if (!id) return undefined

  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) return undefined

  return journey?.blocks?.[id]
}

export const selectBlockByIdOrScope = (
  state: WritableAppState,
  id: BlockId | undefined,
  scope?: string
) => {
  if (typeof id !== 'undefined') return selectBlockById(state, id)

  const name = getBlockNameByScope(scope)

  if (!name) return undefined

  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) return undefined

  const currentStep = selectCurrentStep(state)

  if (!currentStep) return undefined

  return selectBlocksMapCache(state)?.get(currentStep.orderIndex)?.get(name)
}

export const selectStepById = (
  state: WritableAppState,
  id: StepId | undefined
) => {
  if (!id) return undefined

  const journey = selectJourney(state)

  if (isLauncherJourney(journey)) return undefined

  return journey?.steps?.[id]
}

export const selectStepByOrderIndex = (
  state: WritableAppState,
  orderIndex: number
) => {
  return selectStepsKeyedByOrderIndex(state)[orderIndex]
}

/**
 * Selects context parameter values by key while memoizing the computation for performance reasons.
 * The computation happens whenever the context parameters change.
 */
export const selectContextParametersByKey = (state: WritableAppState) => {
  const journey = selectJourney(state)

  if (!journey || isLauncherJourney(journey)) return undefined

  return computeContextParametersByKeyWithMemo(journey.contextParameters)
}

const computeContextParametersByKeyWithMemo = memoLastCall(
  (contextParameters: Journey['contextParameters']) => {
    return objectify(
      Object.values(contextParameters ?? {}),
      (item) => item.paramKey
    )
  }
)

export const selectLauncherJourneyId = (state: WritableAppState) => {
  if (selectIsRootLauncherJourney(state)) {
    return selectRoot(state)?.journey?.id
  }

  return undefined
}
